import { useMemo, useState } from "react";
import TextInput from "../../components/TextInput/TextInput";
import clsx from "clsx";
import Button from "../../components/Button/Button";
import { MdDoneAll, MdOutlinePriorityHigh, MdPercent } from "react-icons/md";
import InputDialog from "../../components/InputDialog/InputDialog";
import { CONFIRM_DIALOG_ICON_SIZE } from "../../constants/icons";
import { useTranslation } from "react-i18next";
import { ICON_SIZES } from "../../helpers/constants";
import CollapsibleBlock from "../../components/CollapsibleBlock/CollapsibleBlock";
import useMobile from "../../hooks/useMobile";
import useCollapsibles, {
  ETransactionPageMobileCollapsibles,
} from "../../hooks/useCollapsibles";
import useCurrentAccount from "../../hooks/useCurrentAccount";
import { formatCurrency } from "../../helpers/utils";

type TransactionTotalsType = {
  isDetailed?: boolean;
  taxes?: string;
  discount?: string;
  subTotal?: string;
  total?: string;
  onTaxesChange: (taxes: string) => void;
  onDiscountChange: (discount: string) => void;
  onSubTotalChange: (subTotal: string) => void;
};

const TransactionTotals: React.FC<TransactionTotalsType> = ({
  isDetailed = false,
  taxes,
  discount,
  subTotal,
  total,
  onTaxesChange,
  onDiscountChange,
  onSubTotalChange,
}) => {
  const { t } = useTranslation();
  const { isMobile } = useMobile();
  const { currencySymbol } = useCurrentAccount();
  const {
    toggleTransactionPageMobileCollapsible,
    isTransactionPageMobileCollapsibleOpen,
    collapsiblePreferences,
    updateCollapsible,
  } = useCollapsibles();

  const [showTaxesCalc, setShowTaxesCalc] = useState<boolean>(false);
  const [showDiscountCalc, setShowDiscountCalc] = useState<boolean>(false);

  const totalsDataIsValid = useMemo(() => {
    return subTotal !== "" && total !== "";
  }, [subTotal, total]);

  return (
    <>
      <CollapsibleBlock
        title={t("transaction_page.collapsibles.totals.title")}
        icon={
          totalsDataIsValid ? (
            <MdDoneAll size={ICON_SIZES.SM} className="text-lime-400" />
          ) : (
            <MdOutlinePriorityHigh
              size={ICON_SIZES.SM}
              className="text-red-500 dark:text-rose-400/80"
            />
          )
        }
        uppercase
        open={
          isMobile
            ? isTransactionPageMobileCollapsibleOpen(
                ETransactionPageMobileCollapsibles.TOTALS
              )
            : collapsiblePreferences.transactionAmountsOpen
        }
        onChange={(isOpen: boolean) => {
          if (isMobile) {
            toggleTransactionPageMobileCollapsible(
              ETransactionPageMobileCollapsibles.TOTALS
            );
          } else {
            updateCollapsible({
              transactionAmountsOpen: isOpen,
            });
          }
        }}
        badge={
          totalsDataIsValid
            ? formatCurrency({ value: total || 0, currency: currencySymbol })
            : t("fields_validation.incomplete.badge")
        }
        badgeKind={totalsDataIsValid ? "info" : "danger"}
      >
        <div className={clsx(["flex", "flex-col", "gap-4", "w-full"])}>
          <div className={clsx(["flex", "flex-col", "gap-2", "w-full"])}>
            <TextInput
              required
              placeholder={t(
                "transaction_page.collapsibles.totals.fields.subtotal_placeholder"
              )}
              value={subTotal}
              readOnly={isDetailed}
              onChange={(e: any) => {
                if (!isDetailed) {
                  onSubTotalChange(e?.target?.value || "");
                }
              }}
              label={t("transaction_page.collapsibles.totals.fields.subtotal")}
              type="number"
              className={clsx([
                "!text-xl",
                "font-medium",
                "placeholder:!text-xl",
                "placeholder:font-medium",
              ])}
              autoComplete="transaction-amount"
            />
            <p className="hidden md:block text-sm text-gray-500">
              {isDetailed
                ? t("transaction_page.subtotal_detailed_help_text")
                : t("transaction_page.subtotal_help_text")}
            </p>
          </div>

          <div
            className={clsx([
              "flex",
              "w-full",
              "gap-4",
              "flex-row",
              "md:flex-col",
            ])}
          >
            <div
              className={clsx([
                "flex",
                "flex-row",
                "gap-2",
                "items-end",
                "w-1/2",
                "md:w-full",
              ])}
            >
              <TextInput
                placeholder={t(
                  "transaction_page.collapsibles.totals.fields.taxes_placeholder"
                )}
                value={taxes}
                onChange={(e: any) => onTaxesChange(e?.target?.value || "")}
                label={t("transaction_page.collapsibles.totals.fields.taxes")}
                type="number"
                autoComplete="transaction-amount"
              />
              <div className={clsx(["flex", "justify-end"])}>
                <Button
                  onClick={() => setShowTaxesCalc(true)}
                  icon={<MdPercent size={ICON_SIZES.MD} />}
                  tooltip={t("shared.calculate")}
                />
              </div>
            </div>

            <div
              className={clsx([
                "flex",
                "flex-row",
                "gap-2",
                "items-end",
                "w-1/2 md:w-full",
              ])}
            >
              <TextInput
                placeholder={t(
                  "transaction_page.collapsibles.totals.fields.discount_placeholder"
                )}
                value={discount}
                onChange={(e: any) => onDiscountChange(e?.target?.value || "")}
                label={t(
                  "transaction_page.collapsibles.totals.fields.discount"
                )}
                type="number"
                autoComplete="transaction-amount"
              />
              <div className={clsx(["flex", "justify-end"])}>
                <Button
                  onClick={() => setShowDiscountCalc(true)}
                  icon={<MdPercent size={ICON_SIZES.MD} />}
                  tooltip={t("shared.calculate")}
                />
              </div>
            </div>
          </div>

          <TextInput
            placeholder={t(
              "transaction_page.collapsibles.totals.fields.total_placeholder"
            )}
            value={total}
            onChange={() => {}}
            label={t("transaction_page.collapsibles.totals.fields.total")}
            type="number"
            inputMode="decimal"
            autoComplete="transaction-amount"
          />
        </div>
      </CollapsibleBlock>
      {showTaxesCalc && (
        <InputDialog
          description="Digita el porcentaje de impuestos que deseas aplicar."
          placeholder="Impuestos"
          title="Impuestos"
          onClose={() => setShowTaxesCalc(false)}
          icon={<MdPercent size={CONFIRM_DIALOG_ICON_SIZE} />}
          onConfirm={(answer: string) => {
            const percent = Number(answer);
            const calculation = (Number(subTotal) * Number(percent)) / 100;

            onTaxesChange(calculation.toString());
            setShowTaxesCalc(false);
          }}
        />
      )}
      {showDiscountCalc && (
        <InputDialog
          description="Digita el porcentaje de descuento que deseas aplicar."
          title="Descuento"
          placeholder="Descuento"
          icon={<MdPercent size={CONFIRM_DIALOG_ICON_SIZE} />}
          onClose={() => setShowDiscountCalc(false)}
          onConfirm={(answer: string) => {
            const percent = Number(answer);
            const calculation = (Number(subTotal) * Number(percent)) / 100;

            onDiscountChange(calculation.toString());
            setShowDiscountCalc(false);
          }}
        />
      )}
    </>
  );
};

export default TransactionTotals;
